/**
 * Copy rules for NewCitationPing.
 *
 * The template owns its wording rather than each ping subclass supplying a sentence, so all
 * five mentions pings read identically and only the underlying mention's data varies. Every
 * string swaps "mention" for "citation" when the mention is classified as a citation by
 * Mentions::Citation on the server.
 */

import indefinite from 'indefinite';

// Shown in place of a research interest when the mentioning work carries no usable tag.
export const TOPIC_FALLBACK = 'academic';

export interface NewCitationPingCopy {
  // Whether the server classified this mention as a citation.
  isCitation?: boolean;
  // Display name of the mentioning author; absent for mentions we can't attribute, and
  // deliberately withheld when the citing-author killswitch or blocklist applies.
  citingAuthorName?: string | null;
  // The viewer's own name, in the form that was actually matched in the citing text.
  mentionedName: string;
  // Research interest of the mentioning work.
  topic?: string | null;
}

export function pingHeader({ isCitation }: NewCitationPingCopy): string {
  return isCitation ? 'New Citations' : 'New Mentions';
}

export interface BodySegment {
  text: string;
  // The values drawn from the mention — citing author, the viewer's name, the topic — which
  // the design emphasises against the surrounding sentence.
  dynamic?: boolean;
}

/**
 * The body sentence split into static and dynamic runs, so the component can emphasise the
 * values without parsing a rendered string.
 */
export function pingBodyParts({
  isCitation,
  citingAuthorName,
  mentionedName,
  topic,
}: NewCitationPingCopy): BodySegment[] {
  const verb = isCitation ? 'cited' : 'mentioned';
  const subject = topic?.trim() || TOPIC_FALLBACK;
  const author = citingAuthorName?.trim();
  // Handles the cases a plain vowel test gets wrong, e.g. "a university", "an hour".
  const article = indefinite(subject, { articleOnly: true });

  if (author) {
    return [
      { text: author, dynamic: true },
      { text: ` ${verb} ` },
      { text: mentionedName, dynamic: true },
      { text: ` in ${article} ` },
      { text: subject, dynamic: true },
      { text: ' paper' },
    ];
  }

  return [
    { text: mentionedName, dynamic: true },
    { text: ` was ${verb} in ${article} ` },
    { text: subject, dynamic: true },
    { text: ' paper' },
  ];
}

/** The same sentence as plain text, for anywhere that can't render segments. */
export function pingBody(copy: NewCitationPingCopy): string {
  return pingBodyParts(copy)
    .map((segment) => segment.text)
    .join('');
}

export function pingButtonLabel({ isCitation }: NewCitationPingCopy): string {
  return isCitation ? 'View your citations' : 'View your mentions';
}
